import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AppNav } from "@/components/AppNav";
import { SignaturePad } from "@/components/SignaturePad";
import { ContractDocument } from "@/components/ContractDocument";
import { SelfieImage } from "@/components/SelfieImage";
import { downloadPartnerPdf } from "@/lib/contractPdf";
import { STATUS_META, PARTY1, DEFAULT_COMPENSATION } from "@/lib/partner";
import { readFileAsDataURL } from "@/lib/month";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Handshake, Loader2, FileDown, Link2, Trash2, PenLine, Upload, Check } from "lucide-react";

const EMPTY = {
  partner_name: "", partner_title: "Owner", partner_contact: "", partner_email: "", partner_company: "",
  property_name: "", property_address: "", compensation_text: DEFAULT_COMPENSATION, effective_date: "",
  party1_name: PARTY1.name, party1_title: PARTY1.title, party1_contact: PARTY1.contact, party1_company: PARTY1.company,
};

function PartnerFormFields({ form, upd }) {
  return (
    <>
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Bedsolving Indonesia (Party 1)</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.party1_name} onChange={(e) => upd("party1_name", e.target.value)} data-testid="party1-name-input" /></div>
          <div className="space-y-1.5"><Label>Title</Label><Input value={form.party1_title} onChange={(e) => upd("party1_title", e.target.value)} data-testid="party1-title-input" /></div>
          <div className="space-y-1.5"><Label>Contact</Label><Input value={form.party1_contact} onChange={(e) => upd("party1_contact", e.target.value)} data-testid="party1-contact-input" /></div>
          <div className="space-y-1.5"><Label>Company / brand</Label><Input value={form.party1_company} onChange={(e) => upd("party1_company", e.target.value)} data-testid="party1-company-input" /></div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Owner name</Label><Input value={form.partner_name} onChange={(e) => upd("partner_name", e.target.value)} required data-testid="partner-name-input" /></div>
        <div className="space-y-1.5"><Label>Title</Label><Input value={form.partner_title} onChange={(e) => upd("partner_title", e.target.value)} data-testid="partner-title-input" /></div>
        <div className="space-y-1.5"><Label>Phone / contact</Label><Input value={form.partner_contact} onChange={(e) => upd("partner_contact", e.target.value)} data-testid="partner-contact-input" /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.partner_email} onChange={(e) => upd("partner_email", e.target.value)} data-testid="partner-email-input" /></div>
      </div>
      <div className="space-y-1.5"><Label>Company name</Label><Input value={form.partner_company} onChange={(e) => upd("partner_company", e.target.value)} placeholder="Mezandroom" data-testid="partner-company-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Property name</Label><Input value={form.property_name} onChange={(e) => upd("property_name", e.target.value)} required data-testid="partner-property-input" /></div>
        <div className="space-y-1.5"><Label>Property address</Label><Input value={form.property_address} onChange={(e) => upd("property_address", e.target.value)} data-testid="partner-address-input" /></div>
      </div>
      <div className="space-y-1.5">
        <Label>Compensation / profit sharing (COST AND SHARING)</Label>
        <Textarea value={form.compensation_text} onChange={(e) => upd("compensation_text", e.target.value)} className="resize-none h-28" data-testid="partner-compensation-input" />
      </div>
      <div className="space-y-1.5">
        <Label>Effective date (optional — default: tanggal kontrak dibuat)</Label>
        <Input type="date" value={form.effective_date || ""} onChange={(e) => upd("effective_date", e.target.value)} data-testid="effective-date-input" />
      </div>
    </>
  );
}

function DocUpload({ label, value, onPick, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <label className="flex items-center gap-2 text-xs cursor-pointer rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50" data-testid={testId}>
        {value ? <Check className="h-4 w-4 text-emerald-500" /> : <Upload className="h-4 w-4 text-slate-400" />}
        <span className={value ? "text-emerald-600" : "text-slate-500"}>{value ? "Selected" : "Upload file"}</span>
        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) onPick(await readFileAsDataURL(f)); }} />
      </label>
    </div>
  );
}

function CreatePartnerDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [docs, setDocs] = useState({ ktp: null, bank: null, nib: null });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/partners", { ...form, ...docs });
      toast.success("Partner & draft kontrak dibuat");
      setForm(EMPTY); setDocs({ ktp: null, bank: null, nib: null }); setOpen(false);
      onCreated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-partner-btn"><Plus className="h-4 w-4" /> New partner</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New partner & contract</DialogTitle>
          <DialogDescription>Isi data partner baru + compensation sharing. Draft Partnership Agreement &amp; Letter of Authorization dibuat otomatis dan siap ditandatangani online.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <PartnerFormFields form={form} upd={upd} />
          <div className="grid grid-cols-3 gap-2">
            <DocUpload label="KTP" value={docs.ktp} onPick={(v) => setDocs((d) => ({ ...d, ktp: v }))} testId="upload-ktp" />
            <DocUpload label="Bank account" value={docs.bank} onPick={(v) => setDocs((d) => ({ ...d, bank: v }))} testId="upload-bank" />
            <DocUpload label="NIB" value={docs.nib} onPick={(v) => setDocs((d) => ({ ...d, nib: v }))} testId="upload-nib" />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="save-partner-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create partner & draft"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditPartnerDialog({ partner, onSaved, onClose }) {
  const [form, setForm] = useState({
    partner_name: partner.partner_name || "", partner_title: partner.partner_title || "Owner",
    partner_contact: partner.partner_contact || "", partner_email: partner.partner_email || "",
    partner_company: partner.partner_company || "", property_name: partner.property_name || "",
    property_address: partner.property_address || "", compensation_text: partner.compensation_text || DEFAULT_COMPENSATION,
    effective_date: partner.effective_date || "",
    party1_name: partner.party1_name || PARTY1.name, party1_title: partner.party1_title || PARTY1.title,
    party1_contact: partner.party1_contact || PARTY1.contact, party1_company: partner.party1_company || PARTY1.company,
  });
  const [docs, setDocs] = useState({ ktp: null, bank: null, nib: null });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      for (const k of ["ktp", "bank", "nib"]) if (docs[k]) payload[k] = docs[k];
      await api.put(`/partners/${partner.id}`, payload);
      toast.success("Draft kontrak diperbarui");
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Edit draft — {partner.property_name}</DialogTitle>
        <DialogDescription>Ubah bagian mana pun (pihak partner maupun pihak Bedsolving) sebelum kontrak selesai ditandatangani.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <PartnerFormFields form={form} upd={upd} />
        <div className="grid grid-cols-3 gap-2">
          <DocUpload label="KTP (ganti)" value={docs.ktp} onPick={(v) => setDocs((d) => ({ ...d, ktp: v }))} testId="edit-upload-ktp" />
          <DocUpload label="Bank (ganti)" value={docs.bank} onPick={(v) => setDocs((d) => ({ ...d, bank: v }))} testId="edit-upload-bank" />
          <DocUpload label="NIB (ganti)" value={docs.nib} onPick={(v) => setDocs((d) => ({ ...d, nib: v }))} testId="edit-upload-nib" />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="submit" className="flex-1" disabled={saving} data-testid="save-edit-partner-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
        </div>
      </form>
    </DialogContent>
  );
}

function PartnerDetail({ partner, onChanged }) {
  const [sig, setSig] = useState(null);
  const [signing, setSigning] = useState(false);
  const signUrl = `${window.location.origin}/sign/${partner.token}`;

  const adminSign = async () => {
    if (!sig) return toast.error("Gambar tanda tangan dulu");
    setSigning(true);
    try {
      await api.post(`/partners/${partner.id}/admin-sign`, { signature: sig });
      toast.success("Ditandatangani oleh Bedsolving");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSigning(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(signUrl); toast.success("Signing link disalin"); };

  return (
    <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex items-center gap-2">
          <DialogTitle>{partner.property_name}</DialogTitle>
          <Badge className={STATUS_META[partner.status]?.cls}>{STATUS_META[partner.status]?.label}</Badge>
        </div>
        <DialogDescription>{partner.partner_name} · {partner.partner_company || "—"}</DialogDescription>
      </DialogHeader>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => downloadPartnerPdf(partner)} data-testid="download-pdf-btn"><FileDown className="h-4 w-4" /> Download PDF</Button>
        <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-sign-link-btn"><Link2 className="h-4 w-4" /> Copy partner signing link</Button>
      </div>

      {(partner.ktp_path || partner.bank_path || partner.nib_path) && (
        <div className="flex gap-3">
          {[["KTP", partner.ktp_path], ["Bank", partner.bank_path], ["NIB", partner.nib_path]].filter(([, p]) => p).map(([label, path]) => (
            <div key={label} className="text-center">
              <SelfieImage path={path} className="h-16 w-24 rounded object-cover ring-1 ring-slate-200" />
              <span className="text-[10px] text-slate-500">{label}</span>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="agreement" className="mt-1">
        <TabsList>
          <TabsTrigger value="agreement" data-testid="tab-agreement">Partnership Agreement</TabsTrigger>
          <TabsTrigger value="authorization" data-testid="tab-authorization">Letter of Authorization</TabsTrigger>
        </TabsList>
        <TabsContent value="agreement"><ContractDocument type="agreement" data={partner} /></TabsContent>
        <TabsContent value="authorization"><ContractDocument type="authorization" data={partner} /></TabsContent>
      </Tabs>

      <div className="rounded-lg border border-slate-200 p-3">
        <p className="text-sm font-medium mb-2 flex items-center gap-1.5"><PenLine className="h-4 w-4" /> Bedsolving (Party 1) signature</p>
        {partner.admin_signature ? (
          <div className="flex items-center gap-2"><img src={partner.admin_signature} alt="signed" className="h-16 object-contain" /><Badge className="bg-emerald-100 text-emerald-700">Signed {partner.admin_signed_at ? new Date(partner.admin_signed_at).toLocaleDateString("en-GB") : ""}</Badge></div>
        ) : (
          <>
            <SignaturePad onChange={setSig} testId="admin-signature-pad" />
            <Button size="sm" className="mt-2" onClick={adminSign} disabled={signing} data-testid="admin-sign-btn">{signing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign as Bedsolving"}</Button>
          </>
        )}
      </div>
    </DialogContent>
  );
}

export default function PartnersPage() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [delId, setDelId] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/partners"); setPartners(data.partners); }
    catch { toast.error("Failed to load partners"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectedPartner = partners.find((p) => p.id === selected);
  const editingPartner = partners.find((p) => p.id === editing);

  const del = async () => {
    try { await api.delete(`/partners/${delId}`); toast.success("Partner deleted"); setDelId(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#1F1B16] dark-scroll text-slate-100">
      <AppNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 text-white"><Handshake className="h-6 w-6 text-indigo-400" /> Partners & Contracts</h1>
            <p className="text-sm text-slate-400">Draft contract &amp; authorization letter yang bisa ditandatangani online</p>
          </div>
          <CreatePartnerDialog onCreated={load} />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-indigo-400" /></div>
        ) : partners.length === 0 ? (
          <p className="text-center text-slate-500 py-20" data-testid="partners-empty">Belum ada partner. Klik "New partner" untuk membuat kontrak baru.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {partners.map((p) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-xl p-4 bg-slate-900 border border-slate-800 hover:border-indigo-500 transition-all" data-testid={`partner-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-white truncate">{p.property_name}</p>
                    <p className="text-sm text-slate-400 truncate">{p.partner_name}</p>
                  </div>
                  <Badge className={STATUS_META[p.status]?.cls}>{STATUS_META[p.status]?.label}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-2 truncate">{p.partner_company || "—"} · {p.partner_contact || "—"}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 flex-1" onClick={() => setSelected(p.id)} data-testid={`open-partner-${p.id}`}>Open</Button>
                  {p.status !== "completed" && (
                    <Button size="sm" variant="outline" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => setEditing(p.id)} data-testid={`edit-partner-${p.id}`}><PenLine className="h-4 w-4" /></Button>
                  )}
                  <Button size="sm" variant="outline" className="bg-transparent border-rose-900/60 text-rose-400 hover:bg-slate-800" onClick={() => setDelId(p.id)} data-testid={`delete-partner-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          {selectedPartner && <PartnerDetail partner={selectedPartner} onChanged={load} />}
        </Dialog>

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          {editingPartner && <EditPartnerDialog partner={editingPartner} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}
        </Dialog>

        <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete partner?</AlertDialogTitle>
              <AlertDialogDescription>Kontrak partner ini akan dihapus. Property yang sudah dibuat tidak ikut terhapus.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={del} data-testid="confirm-delete-partner">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
